// src/components/sourcex/TableSizeModal.jsx
//
// "How big is this table?" — the first step of adding a Table field.
//
// WHY: picking Table used to drop the coordinator straight into a stack of
// column cards with no table in sight, so the shape of the thing they were
// building was never on screen. Word and Google Docs both ask the same one
// question first — how many rows and columns — and then show you the grid.
// This is that step, hover-picker and all.
//
// It is deliberately the ONLY thing this modal asks. Row behaviour (a fixed
// set of rows vs. a log staff add to), column types, labels and starting
// values are all decided afterwards ON the grid, where they can be changed
// again at any point. Nothing chosen here is permanent — rows and columns are
// added, inserted and removed freely once the grid is up.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Table2, X } from "lucide-react";

// Room to build a real source-document table without letting a typo (or a
// held-down arrow key) produce a 900-column grid that locks up the editor.
// Rows stay well inside templateSchema's 100-row cap for rowLabels/prefillRows.
export const MAX_TABLE_ROWS = 50;
export const MAX_TABLE_COLS = 20;

// How much of the grid the hover picker offers directly. Bigger than that is
// still reachable — through the two number boxes underneath.
const PICKER_ROWS = 8;
const PICKER_COLS = 10;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/**
 * A number box that only clamps once the value is committed (blur/Enter),
 * never mid-typing. A plain controlled number input that re-clamps on every
 * keystroke can't be cleared and retyped — clearing gives "", which clamps
 * straight back to 1, and the next digit lands after that 1.
 */
function SizeBox({ id, label, value, max, onChange }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);

  const commit = () => {
    const parsed = parseInt(text, 10);
    const next = Number.isNaN(parsed) ? value : clamp(parsed, 1, max);
    setText(String(next));
    if (next !== value) onChange(next);
  };

  return (
    <label htmlFor={id} className="flex items-center gap-2">
      <span className="text-[11px] text-slate-400">{label}</span>
      <input
        id={id}
        type="number"
        min={1}
        max={max}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
        className="w-16 text-center px-2 py-1.5 rounded-lg bg-white/[0.06] border border-white/12 text-sm text-slate-100 outline-none focus:border-purple-300/60 transition [color-scheme:dark]"
      />
    </label>
  );
}

/**
 * @param {number} [props.initialRows]
 * @param {number} [props.initialCols]
 * @param {(size: {rows: number, cols: number}) => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
export default function TableSizeModal({
  initialRows = 3,
  initialCols = 3,
  onConfirm,
  onCancel,
}) {
  const [rows, setRows] = useState(clamp(initialRows, 1, MAX_TABLE_ROWS));
  const [cols, setCols] = useState(clamp(initialCols, 1, MAX_TABLE_COLS));
  // What the pointer/keyboard focus is currently over in the picker, which
  // previews a size without committing it — exactly how Word's picker reads.
  const [hover, setHover] = useState(null);

  const shownRows = hover ? hover.r : rows;
  const shownCols = hover ? hover.c : cols;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const pick = (r, c) => onConfirm({ rows: r, cols: c });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Insert a table"
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/12 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_24px_70px_-20px_rgba(244,63,94,0.5)] overflow-hidden">
        <div className="flex items-start gap-2.5 px-4 pt-4">
          <span className="shrink-0 w-8 h-8 rounded-lg border border-rose-400/25 bg-rose-400/10 text-rose-300 flex items-center justify-center">
            <Table2 className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-white leading-snug">
              How big is this table?
            </h3>
            <p className="mt-0.5 text-[11px] text-slate-400">
              Pick a starting size — you can add or remove rows and columns
              afterwards.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 pt-3">
          <div
            className="inline-block rounded-xl border border-white/10 bg-white/[0.03] p-2"
            onMouseLeave={() => setHover(null)}
          >
            {Array.from({ length: PICKER_ROWS }).map((_, ri) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={ri} className="flex">
                {Array.from({ length: PICKER_COLS }).map((__, ci) => {
                  const on = ri < shownRows && ci < shownCols;
                  return (
                    <button
                      // eslint-disable-next-line react/no-array-index-key
                      key={ci}
                      type="button"
                      aria-label={`${ri + 1} rows by ${ci + 1} columns`}
                      onMouseEnter={() => setHover({ r: ri + 1, c: ci + 1 })}
                      onFocus={() => setHover({ r: ri + 1, c: ci + 1 })}
                      onClick={() => pick(ri + 1, ci + 1)}
                      className={`w-[22px] h-[19px] m-[1px] rounded-[3px] border transition ${
                        on
                          ? "bg-rose-400/40 border-rose-300/60"
                          : "bg-white/[0.05] border-white/10 hover:bg-white/[0.12]"
                      }`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          <div className="mt-1.5 text-[11px] text-slate-400 tabular-nums">
            {shownRows} row{shownRows === 1 ? "" : "s"} × {shownCols} column
            {shownCols === 1 ? "" : "s"}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-4 pt-3">
          <SizeBox
            id="table-size-rows"
            label="Rows"
            value={rows}
            max={MAX_TABLE_ROWS}
            onChange={setRows}
          />
          <SizeBox
            id="table-size-cols"
            label="Columns"
            value={cols}
            max={MAX_TABLE_COLS}
            onChange={setCols}
          />
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 mt-3 border-t border-white/10 bg-white/[0.02]">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 rounded-xl text-sm text-slate-300 bg-white/[0.05] border border-white/10 hover:bg-white/[0.12] transition"
          >
            Cancel
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => pick(rows, cols)}
            className="px-3.5 py-2 rounded-xl text-sm font-medium text-white shadow bg-gradient-to-r from-rose-500 to-red-500 hover:brightness-110 transition"
          >
            Insert table
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
